import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    IconButton,
    Divider,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Chip,
    OutlinedInput,
    List,
    ListItemButton,
    ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { fetchAllKeywords, fetchKnowledgeByKeywords, fetchAllClassifiers, fetchKnowledgeByClassifiers } from '../api/knowledgeApi';
import { createCopy } from '../api/copyApi';

const AddPromptModal = ({ open, onClose, onSuccess }) => {
    const [userPrompt, setUserPrompt] = useState('');
    const [wordCount, setWordCount] = useState('');
    const [keywords, setKeywords] = useState([]);
    const [selectedKeywords, setSelectedKeywords] = useState([]);
    const [classifiers, setClassifiers] = useState([]);
    const [selectedClassifiers, setSelectedClassifiers] = useState([]);
    const [knowledgeSummaries, setKnowledgeSummaries] = useState([]);
    const [selectedKnowledge, setSelectedKnowledge] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch all keywords and classifiers when the modal opens
    useEffect(() => {
        if (open) {
            setUserPrompt('');
            setWordCount('');
            setSelectedKeywords([]);
            setSelectedClassifiers([]);
            setKnowledgeSummaries([]);
            setSelectedKnowledge([]);
            setError(null);
            fetchKeywords();
            fetchClassifiers();
        }
    }, [open]);

    const fetchKeywords = async () => {
        try {
            const data = await fetchAllKeywords();
            setKeywords(data);
        } catch (err) {
            console.error('Error fetching keywords:', err);
            setError('Failed to fetch keywords');
        }
    };

    const fetchClassifiers = async () => {
        try {
            const data = await fetchAllClassifiers();
            setClassifiers(data);
        } catch (err) {
            console.error('Error fetching classifiers:', err);
            setError('Failed to fetch classifiers');
        }
    };

    const handleKeywordChange = (event) => {
        const value = event.target.value;
        console.log('Selected keywords:', value);
        setSelectedKeywords(value);
    };

    const handleClassifierChange = (event) => {
        const value = event.target.value;
        console.log('Selected classifiers:', value);
        setSelectedClassifiers(value);
    };

    // Fetch knowledge summaries when selected keywords or classifiers change
    useEffect(() => {
        const fetchKnowledgeSummaries = async () => {
            setLoading(true);
            setError(null);
            try {
                if ((!selectedKeywords || selectedKeywords.length === 0) && 
                    (!selectedClassifiers || selectedClassifiers.length === 0)) {
                    setKnowledgeSummaries([]);
                    return;
                }

                let knowledgeData = [];
                
                // Fetch knowledge from keywords if any are selected
                if (selectedKeywords && selectedKeywords.length > 0) {
                    console.log('Fetching knowledge for keywords:', selectedKeywords);
                    const keywordData = await fetchKnowledgeByKeywords(selectedKeywords);
                    knowledgeData = [...knowledgeData, ...keywordData];
                }

                // Fetch knowledge from classifiers if any are selected
                if (selectedClassifiers && selectedClassifiers.length > 0) {
                    console.log('Fetching knowledge for classifiers:', selectedClassifiers);
                    const classifierData = await fetchKnowledgeByClassifiers(selectedClassifiers);
                    knowledgeData = [...knowledgeData, ...classifierData];
                }

                // Remove duplicates based on id
                const uniqueKnowledge = Array.from(new Map(knowledgeData.map(item => [item.id, item])).values());
                console.log('Combined knowledge data:', uniqueKnowledge);
                setKnowledgeSummaries(uniqueKnowledge);
            } catch (err) {
                console.error('Error fetching knowledge summaries:', err);
                setError('Failed to fetch knowledge summaries');
            } finally {
                setLoading(false);
            }
        };

        fetchKnowledgeSummaries();
    }, [selectedKeywords, selectedClassifiers]);

    const handleSave = async () => {
        setLoading(true);
        setError(null);
        try {
            const creationDate = new Date().toISOString();
            const userIdentifier = 'string';
            const userName = 'string';

            const copyData = {
                id: 0,
                creationDate,
                userPrompt,
                userIdentifier,
                userName,
                userSystemPrompt: userPrompt,
                keyWords: selectedKeywords.map((kw) => ({
                    id: kw.id || 0,
                    keywordText: kw.keywordText,
                    userIdentifier,
                    userName,
                    creationDate
                })),
                classifiers: selectedClassifiers.map((cls) => ({
                    id: cls.id || 0,
                    classText: cls.classText,
                    userIdentifier,
                    userName,
                    creationDate
                })),
                knowledge: selectedKnowledge.map((kn) => ({
                    id: kn.id || 0,
                    knowledgeText: kn.knowledgeText,
                    knowledgeSummary: kn.knowledgeSummary,
                    userIdentifier,
                    userName,
                    creationDate: kn.creationDate || creationDate
                }))
            };

            await createCopy(copyData);
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Failed to create copy:', err);
            setError('Failed to create copy');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="add-prompt-modal-title"
            aria-describedby="add-prompt-modal-description"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: 600,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 2,
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Typography id="add-prompt-modal-title" variant="h6" component="h2">
                        Create User Prompt
                    </Typography>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />

                {/* User Prompt Input */}
                <TextField
                    label="User Prompt"
                    value={userPrompt}
                    onChange={(e) => setUserPrompt(e.target.value)}
                    variant="outlined"
                    fullWidth
                    sx={{ mt: 2 }}
                />

                {/* Word Count Selection */}
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="word-count-label">Word Count</InputLabel>
                    <Select
                        labelId="word-count-label"
                        value={wordCount}
                        onChange={(e) => setWordCount(e.target.value)}
                        label="Word Count"
                    >
                        <MenuItem value={250}>250</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                        <MenuItem value={1000}>1000</MenuItem>
                    </Select>
                </FormControl>

                {/* Keywords Multi-select */}
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="keywords-label">Keywords</InputLabel>
                    <Select
                        labelId="keywords-label"
                        multiple
                        value={selectedKeywords}
                        onChange={handleKeywordChange}
                        input={<OutlinedInput label="Keywords" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip key={value.id} label={value.keywordText} />
                                ))}
                            </Box>
                        )}
                    >
                        {keywords.map((keyword) => (
                            <MenuItem key={keyword.id} value={keyword}>
                                {keyword.keywordText}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Classifiers Multi-select */}
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="classifiers-label">Tags (Classifiers)</InputLabel>
                    <Select
                        labelId="classifiers-label"
                        multiple
                        value={selectedClassifiers}
                        onChange={handleClassifierChange}
                        input={<OutlinedInput label="Tags (Classifiers)" />}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                    <Chip key={value.id} label={value.classText} />
                                ))}
                            </Box>
                        )}
                    >
                        {classifiers.map((classifier) => (
                            <MenuItem key={classifier.id} value={classifier}>
                                {classifier.classText}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Knowledge Summaries List */}
                {knowledgeSummaries.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            Select Knowledge Summaries:
                        </Typography>
                        <List>
                            {knowledgeSummaries.map((kn) => (
                                <ListItemButton
                                    key={kn.id}
                                    selected={selectedKnowledge.includes(kn)}
                                    onClick={() =>
                                        setSelectedKnowledge((prev) =>
                                            prev.includes(kn)
                                                ? prev.filter((item) => item !== kn)
                                                : [...prev, kn]
                                        )
                                    }
                                >
                                    <ListItemText primary={kn.knowledgeSummary} />
                                </ListItemButton>
                            ))}
                        </List>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button onClick={onClose} sx={{ mr: 2 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        disabled={
                            loading ||
                            !userPrompt ||
                            !wordCount ||
                            (selectedKeywords.length === 0 && selectedClassifiers.length === 0) ||
                            selectedKnowledge.length === 0
                        }
                    >
                        {loading ? <CircularProgress size={24} /> : 'Create'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default AddPromptModal;
